import { inject, Injectable } from '@angular/core';

import type { ShareBlocks } from '@common/types/site.type';
import type { SiteContent, SiteContentEntity } from '@common/types/site-content.type';
import type { Template } from '@common/types/template.type';

import type { DenormalizedBlockEntity } from '@web/types';

import { structureData } from '@web/store/page-structure/page-structure/mockStructureData';

import { ThemeStateService } from '@sites/site-theme/src/state';
import type { ThemeSettingsState } from '@sites/site-theme/src/state';
import { PageStructureService } from '@web/store/page-structure/page-structure/page-structure.service';

import { ContentFacade } from './content.facade';
import { readThemeSettings } from '@sites/site-theme';

@Injectable({
    providedIn: 'root',
})
export class ContentService {
    private readonly pageStructureService: PageStructureService = inject(PageStructureService);
    private readonly themeState = inject(ThemeStateService);
    private readonly contentFacade: ContentFacade = inject(ContentFacade);

    public setInitialContent(
        siteContent: SiteContentEntity,
        pageContent: SiteContentEntity,
        isNewSite: boolean = false
    ): void {
        const pageStructure = this.getPageContentStructure(pageContent);
        const sharedBlocks = this.getSiteContentSharedBlocks(siteContent);
        const normalizedPageStructure = this.pageStructureService.normalizeStructure(pageStructure, sharedBlocks);
        const themeSettings = this.getSiteContentThemeSettings(siteContent);

        this.setStructure(normalizedPageStructure);
        this.setThemeSettings(themeSettings, isNewSite);
    }

    public setPublishedContent(siteContent: SiteContentEntity, pageContent: SiteContentEntity): void {
        const normalizedPageStructure = this.pageStructureService.normalizeStructure(
            JSON.parse(pageContent.structure) as DenormalizedBlockEntity[],
            siteContent.shared_blocks
        );

        this.setStructure(normalizedPageStructure);
        this.setThemeSettings(readThemeSettings(siteContent.theme_settings) as ThemeSettingsState);

        // The draft becomes the published version, the theme as well as the shared blocks: a save
        // reads the draft theme to decide whether it can write back what it holds.
        this.contentFacade.updateSiteThemeSettingsContent(siteContent.theme_settings);
        this.contentFacade.setSharedBlocks(siteContent.shared_blocks);
        this.contentFacade.triggerSaveContent();
    }

    private setStructure(structure: DenormalizedBlockEntity[]): void {
        this.pageStructureService.setStructure(structure);
    }

    private setThemeSettings(themeSettings: ThemeSettingsState, isNewSite: boolean = false): void {
        this.themeState.initTheme(themeSettings, isNewSite);
    }

    public getPageContentStructure(content: SiteContentEntity): DenormalizedBlockEntity[] {
        if (content.draft_structure) {
            return JSON.parse(content.draft_structure) as DenormalizedBlockEntity[];
            // return JSON.parse(content.draft_structure);
        }

        if (content.structure) {
            return JSON.parse(content.structure) as DenormalizedBlockEntity[];
        }

        return [];
    }

    public getSiteContentSharedBlocks(content: SiteContentEntity): ShareBlocks {
        if (content.draft_shared_blocks) {
            return content.draft_shared_blocks;
        }

        return content.shared_blocks;
    }

    /**
     * Read through `@sites/site-theme` rather than `JSON.parse`, so a page saved before the theme
     * was versioned is migrated on the way in instead of being trusted as-is. The shape handed back
     * is unchanged — the version tag is added at save time, not here.
     */
    public getSiteContentThemeSettings(content: SiteContentEntity): ThemeSettingsState {
        if (content.draft_theme_settings) {
            return readThemeSettings(content.draft_theme_settings) as ThemeSettingsState;
        }

        if (content.theme_settings) {
            return readThemeSettings(content.theme_settings) as ThemeSettingsState;
        }

        return {};
    }

    public normalizeSiteContent(content: SiteContent): SiteContentEntity {
        return {
            ...content,
            shared_blocks: this.prepareSiteSharedBlocks(content.shared_blocks),
            draft_shared_blocks: this.prepareSiteSharedBlocks(content.draft_shared_blocks ?? content.shared_blocks),
        };
    }

    public normalizePageContent(content: SiteContent): SiteContentEntity {
        return { ...content } as any;
    }

    private getDefaultSharedBlocks(): ShareBlocks {
        return {
            header: null,
            footer: null,
        };
    }

    private prepareSiteSharedBlocks(sharedBlocks: ShareBlocks | string): ShareBlocks {
        if (!sharedBlocks) {
            return this.getDefaultSharedBlocks();
        }

        if (typeof sharedBlocks === 'string') {
            return JSON.parse(sharedBlocks) as ShareBlocks;
        }

        return sharedBlocks;
    }

    public createEmptySiteContent(): SiteContentEntity {
        return {
            draft_theme_settings: null,
            draft_theme_settings_style: null,
            draft_shared_blocks: this.getDefaultSharedBlocks(),
        };
    }

    public createEmptySiteContentFromTemplate(template: Template): SiteContentEntity {
        return {
            draft_theme_settings: template.contents.theme_settings ?? null,
            draft_theme_settings_style: null,
            draft_shared_blocks: this.getDefaultSharedBlocks(),
        };
    }

    public createEmptyPageContent(): SiteContentEntity {
        return {
            draft_structure: JSON.stringify([]),
        };
    }

    public createEmptyPageContentFromTemplate(template: Template): SiteContentEntity {
        return {
            draft_structure: template.contents.structure ?? JSON.stringify(structureData),
        };
    }
}
